import React, { useState, useEffect } from 'react';
import { parseColors, renderColoredText } from '../utils/colorParser';
import './ServerDetail.css';

const ServerDetail = ({ serverId }) => {
  const [serverData, setServerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playersExpanded, setPlayersExpanded] = useState(false);
  const [serverImageError, setServerImageError] = useState(false);

  useEffect(() => {
    if (serverId) {
      fetchServerDetail(serverId);
    }
  }, [serverId]);

  const fetchServerDetail = async (id) => {
    try {
      setLoading(true);
      const response = await fetch(`https://api.vmp.ir/server/api.php?work=singleData2&id=${id}`);
      if (!response.ok) {
        throw new Error('Failed to fetch server details');
      }
      const data = await response.json();
      setServerData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getSteamId64 = (steamHex) => {
    if (!steamHex || !steamHex.startsWith('steam:')) return null;
    const hex = steamHex.replace('steam:', '');
    // Convert hex to decimal - this is the Steam ID64
    const steamId64 = parseInt(hex, 16);
    return steamId64.toString();
  };

  const getSteamAvatarUrl = (steamId64) => {
    return `https://api.vmp.ir/ui/profile-pic.php?steamid=${steamId64}`;
  };

  const openSteamProfile = (steamId64) => {
    window.open(`https://steamcommunity.com/profiles/${steamId64}`, '_blank');
  };

  const goBack = () => {
    window.history.back();
  };

  const handleConnect = () => {
    if (server.connectEndPoints && server.connectEndPoints.length > 0) {
      window.location.href = `vmp://connect/${server.connectEndPoints[0]}`;
    }
  };

  if (loading) {
    return (
      <div className="server-detail-container">
        <div className="loading-fullscreen">
          <div className="loading-spinner-large"></div>
          <p>Loading server details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="server-detail-container">
        <div className="error-fullscreen">
          <h3>Error loading server details</h3>
          <p>{error}</p>
          <button onClick={() => fetchServerDetail(serverId)} className="retry-button-large">
            Try Again
          </button>
          <button onClick={goBack} className="back-button">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!serverData || !serverData.Data) {
    return (
      <div className="server-detail-container">
        <div className="error-fullscreen">
          <h3>Server not found</h3>
          <button onClick={goBack} className="back-button">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const server = serverData.Data;
  const serverName = server.vars?.sv_projectName || 'Unknown Server';
  const coloredServerName = parseColors(serverName);
  const players = server.players || [];
  const upvotePower = server.upvotePower || 0;
  const serverImageUrl = `https://vmp.724548.ir.cdn.ir/saturn/icon/${serverId}-${server?.iconVersion}.png`;

  const getBoostTier = (boosts) => {
    if (boosts >= 500) return 'ultra-premium';
    if (boosts >= 200) return 'premium';
    if (boosts >= 101) return 'flashy';
    if (boosts >= 51) return 'bold';
    if (boosts >= 21) return 'prominent';
    return 'modest';
  };

  const boostTier = getBoostTier(upvotePower);

  return (
    <div className="server-detail-container">
      <div className="server-detail-header">
        <button onClick={goBack} className="back-button">
          ← Back to Server List
        </button>
      </div>

      <div className="server-detail-main">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              {!serverImageError ? (
                <img 
                  src={serverImageUrl} 
                  alt={`${serverName} icon`}
                  style={{ 
                    width: '64px', 
                    height: '64px', 
                    borderRadius: '12px',
                    objectFit: 'cover',
                    flexShrink: 0
                  }}
                  onError={() => setServerImageError(true)}
                />
              ) : (
                <div style={{ 
                  width: '64px', 
                  height: '64px', 
                  backgroundColor: '#374151', 
                  borderRadius: '12px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontSize: '28px',
                  color: '#9ca3af',
                  flexShrink: 0
                }}>
                  📊
                </div>
              )}
              <h1 style={{ fontSize: '24px', margin: 0, lineHeight: '1.2' }}>
                {coloredServerName.length > 0 ? renderColoredText(coloredServerName) : serverName}
              </h1>
              <a 
                href={`https://dev.vmp.ir/boost/${serverId}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`boost-indicator boost-${boostTier} boost-clickable`}
                title={upvotePower === 0 ? "Click to boost this server!" : `${upvotePower} boosts - Click to boost more!`}
                onClick={(e) => e.stopPropagation()}
                style={{ flexShrink: 0 }}
              >
                <span className="boost-icon">🚀</span>
                <span className="boost-count">{upvotePower}</span>
                {upvotePower === 0 && <span className="boost-text">Boost</span>}
              </a>
            </div>
            <div className="server-detail-info" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
              <span className="server-detail-id">#{serverId}</span>
              <span className="server-detail-players">
                {server.clients || 0}/{server.sv_maxclients || server.svMaxclients || 0} Players
              </span>
            </div>
          </div>
          <button 
            onClick={handleConnect}
            disabled={!server?.connectEndPoints || server.connectEndPoints.length === 0}
            className="connect-button"
          >
            {(!server?.connectEndPoints || server.connectEndPoints.length === 0) ? 'Private' : 'Connect'}
          </button>
        </div>
      </div>

      <div className="server-detail-content">
        <div className="server-info-section">
          <h2>Server Information</h2>
          <div className="server-info-grid">
            <div className="info-item">
              <label>Game Type:</label>
              <span>{server.gametype || 'Unknown'}</span>
            </div>
            {server.vars?.sv_projectDesc && (
              <div className="info-item">
                <label>Description:</label>
                <span>{server.vars.sv_projectDesc}</span>
              </div>
            )}
            {server.ownerName && (
              <div className="info-item">
                <label>Owner:</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {server.ownerAvatar && (
                    <img 
                      src={server.ownerAvatar} 
                      alt={`${server.ownerName}'s avatar`}
                      style={{ 
                        width: '32px', 
                        height: '32px', 
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                  <span>{server.ownerName}</span>
                  {server.ownerProfile && (
                    <a 
                      href={server.ownerProfile} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ 
                        color: '#10b981', 
                        textDecoration: 'none',
                        fontSize: '14px',
                        marginLeft: '8px'
                      }}
                      onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                      onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                    >
                      View Profile →
                    </a>
                  )}
                </div>
              </div>
            )}
            {server.vars?.tags && (
              <div className="info-item">
                <label>Tags:</label>
                <div className="server-tags-detail">
                  {server.vars.tags.split(',').map((tag, index) => (
                    <span key={index} className={`server-tag-detail server-tag-${index % 6}`}>
                      {tag.trim()}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="players-section">
          <div className="players-header" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <button 
              className="collapse-button"
              onClick={() => setPlayersExpanded(!playersExpanded)}
              style={{
                background: playersExpanded ? '#ef4444' : '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
              }}
            >
              {playersExpanded ? '▼' : '▶'}
            </button>
            <h2 style={{ margin: 0 }}>Players Online ({players.length})</h2>
          </div>
          {playersExpanded && (
            players.length === 0 ? (
              <div className="no-players">
                <p>No players currently online</p>
              </div>
            ) : (
              <div className="players-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {players.map((player, index) => {
                  const steamIdentifier = player.identifiers?.find(id => id.startsWith('steam:'));
                  const steamId64 = steamIdentifier ? getSteamId64(steamIdentifier) : null;
                  
                  return (
                    <div 
                      key={player.id || index} 
                      className="player-card"
                      onClick={steamId64 ? () => openSteamProfile(steamId64) : undefined}
                      style={{ 
                        cursor: steamId64 ? 'pointer' : 'default',
                        minWidth: 'fit-content',
                        width: 'auto',
                        padding: '8px 12px'
                      }}
                    >
                      <div className="player-info">
                        <span className="player-name">{player.name}</span>
                        <span className="player-ping">Ping: {player.ping}ms</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default ServerDetail;